import React from 'react';
import { Dialog } from '@mui/material';
import { allocateZ } from './zStack';

const BASE_Z = 1290;

const ModalStackContext = React.createContext(BASE_Z);

export function useStackZIndex(override) {
  const parentZ = React.useContext(ModalStackContext);
  if (override != null) return override;
  return parentZ + 10;
}

// For top-level modals: grab a fresh global z whenever the modal opens or its
// identity changes. Most recent opener always sits on top — no manual stack
// bookkeeping in App.js, no `bringModalToFront` calls scattered across the app.
//
//   const myZ = useAutoZ(open, stockNumber);
//
// `key` is anything that should re-allocate while the modal stays mounted
// (e.g. switching from one stock number to another).
export function useAutoZ(open, key) {
  const [z, setZ] = React.useState(BASE_Z);
  React.useEffect(() => {
    if (open) setZ(allocateZ());
  }, [open, key]);
  return z;
}

// For Select / Autocomplete / Menu rendered inside a stacked Dialog.
// Returns MenuProps that lift the popover above the current dialog.
//
// Pass the dialog's z (from useStackZIndex / useAutoZ) if you're calling this
// from the same component that *creates* the dialog (the ModalStackProvider
// hasn't pushed its value yet at that point). Omit the arg when calling from
// a child component already inside the provider.
//
//   const myZ = useStackZIndex();
//   ...
//   <Select MenuProps={useSelectMenuProps(myZ)} ...>
export function useSelectMenuProps(override) {
  const ctxZ = React.useContext(ModalStackContext);
  const myZ = override != null ? override : ctxZ;
  return React.useMemo(() => ({
    PaperProps: { sx: { zIndex: myZ + 1 } },
  }), [myZ]);
}

// Same idea for Autocomplete (uses Popper, not Menu).
//   <Autocomplete slotProps={useAutocompleteSlotProps(myZ)} ...>
export function useAutocompleteSlotProps(override) {
  const ctxZ = React.useContext(ModalStackContext);
  const myZ = override != null ? override : ctxZ;
  return React.useMemo(() => ({
    popper: { sx: { zIndex: myZ + 1 } },
  }), [myZ]);
}

// Same idea for MUI X DatePicker / DateTimePicker / TimePicker. On desktop the
// calendar is a Popper; on touch devices the responsive picker swaps to a
// MobileDatePicker that renders a full Dialog (the `dialog` slot). Both must be
// lifted above the surrounding modal — otherwise the mobile calendar opens
// behind it. Merge with your own slotProps if you have any.
//   <DatePicker slotProps={{ ...useDatePickerSlotProps(myZ), textField: {...} }} />
export function useDatePickerSlotProps(override) {
  const ctxZ = React.useContext(ModalStackContext);
  const myZ = override != null ? override : ctxZ;
  return React.useMemo(() => ({
    popper: { sx: { zIndex: myZ + 1 } },
    dialog: { sx: { zIndex: myZ + 1 } },
  }), [myZ]);
}

export const ModalStackProvider = ModalStackContext.Provider;

// Drop-in replacement for MUI <Dialog> that handles stack z-index automatically.
// - Reads the current modal stack depth and bumps z by 10
// - Provides a ModalStackProvider so nested dialogs stack correctly
// - Forwards all other Dialog props
//
// Use this for ANY new dialog. Do not use raw <Dialog> inside another dialog.
export function StackedDialog({ children, sx, ...dialogProps }) {
  const myZ = useStackZIndex();
  return (
    <ModalStackProvider value={myZ}>
      <Dialog {...dialogProps} sx={{ zIndex: myZ, ...sx }}>
        {children}
      </Dialog>
    </ModalStackProvider>
  );
}

export default ModalStackContext;
